import React,{useEffect,useState} from 'react';
import {Link,Outlet} from 'react-router-dom';
import {legalVaultService} from '../services/legalVaultService';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {FiFileText,FiShield,FiCheckCircle,FiExternalLink,FiMail}=FiIcons;

const safeWebsiteUrl=(value='')=> {
  if (!value) return '';
  try {
    const candidate=/^https?:\/\//i.test(value) ? value : `https://${value}`;
    const url=new URL(candidate);
    return ['https:','http:'].includes(url.protocol) ? url.toString() : '';
  } catch {
    return '';
  }
};

export default function PublicLayout() {
  const [brand,setBrand]=useState(null);

  useEffect(()=> {
    let cancelled=false;
    legalVaultService
      .getDefaultBrandingProfile()
      .then((profile)=> {
        if (!cancelled) setBrand(profile);
      })
      .catch(()=> {
        if (!cancelled) setBrand(null);
      });
    return ()=> {
      cancelled=true;
    };
  },[]);

  const currentYear=new Date().getFullYear();
  const abn=brand?.abn || '83 679 369 932';
  const acn=brand?.acn || '679 369 932';
  const contactEmail=brand?.legalEmail || brand?.supportEmail || brand?.email || 'support@bpasolutions.au';
  const websiteUrl=safeWebsiteUrl(brand?.website || 'www.bpasolutions.au');

  useEffect(()=> {
    const previousTitle=window.document.title;
    window.document.title='BPA Solutions | Contract & Policy Hub';
    return ()=> {
      window.document.title=previousTitle;
    };
  },[]);

  return (
    <div className="min-h-screen flex flex-col bg-[#f5f6f8] text-slate-900">
      <div className="h-1 bg-gradient-to-r from-[#8c6a2f] via-[#d7bb79] to-[#8c6a2f] print:hidden" />

      <header className="bg-[#0b1220] text-white border-b border-white/10 print:hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="min-h-[88px] py-4 flex items-center justify-between gap-6">
            <Link to="/legal" className="flex items-center gap-4 min-w-0 group" aria-label="BPA Solutions Contract & Policy Hub">
              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-white border border-white/15 shadow-lg shadow-black/20 flex items-center justify-center overflow-hidden flex-shrink-0 group-hover:border-[#d7bb79]/70 transition-colors">
                {brand?.logo ? (
                  <img src={brand.logo} alt="BPA Solutions logo" className="w-full h-full object-contain p-1.5" />
                ) : (
                  <span className="text-[#0b1220] font-bold tracking-tight">BPA</span>
                )}
              </div>
              <div className="min-w-0">
                <span className="text-[10px] sm:text-xs font-semibold uppercase tracking-[0.18em] text-[#d7bb79]">BPA Solutions</span>
                <p className="text-base sm:text-lg font-semibold tracking-tight text-white truncate mt-1">Contract & Policy Hub</p>
                <p className="text-xs sm:text-sm text-slate-400 truncate">Contracts, terms, policies and published client documents</p>
              </div>
            </Link>

            <div className="flex items-center gap-2 sm:gap-3">
              <div className="hidden lg:flex items-center gap-2 px-3 py-2 rounded-full border border-emerald-400/20 bg-emerald-400/5 text-emerald-200 text-xs font-medium">
                <SafeIcon icon={FiShield} /> Published BPA Solutions documents
              </div>
              <Link
                to="/legal"
                className="inline-flex items-center gap-2 px-3.5 sm:px-4 py-2.5 rounded-xl bg-white/8 border border-white/15 text-sm font-semibold text-white hover:bg-white/12 hover:border-[#d7bb79]/50 transition-all"
              >
                <SafeIcon icon={FiFileText} />
                <span className="hidden sm:inline">Contract Hub</span>
                <span className="sm:hidden">Hub</span>
              </Link>
            </div>
          </div>
        </div>
      </header>

      <div className="bg-white border-b border-slate-200/80 print:hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2.5 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500">
          <div className="flex items-center gap-2">
            <SafeIcon icon={FiCheckCircle} className="text-emerald-600" />
            <span>Access current BPA Solutions contracts, terms and policies from one place.</span>
          </div>
          <span className="hidden md:inline text-slate-400">Documents may be referenced by Statements of Work and other client agreements.</span>
        </div>
      </div>

      <main className="flex-1 bg-[#f5f6f8] print:bg-white">
        <Outlet />
      </main>

      <footer className="bg-[#0b1220] text-slate-300 border-t border-white/10 print:hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-start">
            <div>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 rounded-lg bg-white/10 border border-white/10 flex items-center justify-center">
                  <SafeIcon icon={FiShield} className="text-[#d7bb79]" />
                </div>
                <div>
                  <p className="text-white font-semibold">BPA Solutions</p>
                  <p className="text-xs text-slate-500">Contract & Policy Hub</p>
                </div>
              </div>
              <p className="text-xs leading-relaxed text-slate-500">Contracts, master terms and conditions, service terms, privacy policies and other published BPA Solutions documents.</p>
            </div>

            <div className="md:text-center">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500 mb-3">Legal identity</p>
              <p className="text-sm text-slate-300 leading-relaxed">BPA Solutions (Business Process Automation Solutions) is a trading name of Anish Malhotra Pty Ltd.</p>
              <p className="text-xs text-slate-500 mt-2">ABN {abn}{acn ? ` · ACN ${acn}` : ''}</p>
            </div>

            <div className="md:text-right">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500 mb-3">Contact</p>
              {contactEmail && (
                <a href={`mailto:${contactEmail}`} className="inline-flex md:flex-row-reverse items-center gap-2 text-sm text-slate-300 hover:text-white transition-colors">
                  <SafeIcon icon={FiMail} /> {contactEmail}
                </a>
              )}
              {websiteUrl && (
                <div className="mt-2">
                  <a href={websiteUrl} target="_blank" rel="noopener noreferrer" className="inline-flex md:flex-row-reverse items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors">
                    <SafeIcon icon={FiExternalLink} /> Website
                  </a>
                </div>
              )}
            </div>
          </div>

          <div className="mt-8 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-slate-500">
            <p>© {currentYear} Anish Malhotra Pty Ltd. All rights reserved.</p>
            <p>BPA Solutions · Business Process Automation Solutions</p>
          </div>
        </div>
      </footer>
    </div>
  );
}